import React from 'react';
import styled from 'styled-components';
import { Wallet, TrendingUp, CreditCard, FileText, AlertTriangle, CheckCircle } from 'lucide-react';

interface FinancialOverview {
  actualIncomeThisMonth: number;
  actualIncomeLastMonth: number;
  actualIncomePercentageChange: number;
  expectedIncomeNextMonth: number;
  expectedIncomePercentageChange: number;
  openDebtsBalance: number;
  openDebtsPercentageChange: number;
  activePaymentPlansCount: number;
  overduePaymentPlansCount: number;
}

interface Props {
  data: FinancialOverview;
}

const Container = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
  padding: 28px;
  margin-bottom: 28px;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
`;

const SectionIcon = styled.div`
  display: flex;
  align-items: center;
  color: var(--primary-color);
  filter: saturate(0.7) brightness(0.9);
`;

const SectionTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
`;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 767px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div<{ $accentColor: string }>`
  background: white;
  border-radius: 12px;
  padding: 24px;
  padding-top: 28px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  border: 1px solid var(--border-color);
  position: relative;
  overflow: hidden;
  transition: all 0.2s ease;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${props => props.$accentColor};
    filter: saturate(0.7) brightness(0.9);
  }

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`;

const CardIcon = styled.div<{ $color: string; $noBackground?: boolean }>`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: ${props => props.$noBackground ? 'transparent' : `${props.$color}15`};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.$color};
  filter: saturate(0.7) brightness(0.9);
`;

const CardTitle = styled.div`
  font-size: 14px;
  color: var(--text-secondary);
  font-weight: 500;
`;

const CardValue = styled.div`
  font-size: 28px;
  font-weight: 700;
  color: var(--text-primary);
  direction: ltr;
  text-align: right;
  margin-bottom: 8px;

  @media (max-width: 767px) {
    font-size: 22px;
  }
`;

const CardSubValue = styled.div`
  font-size: 13px;
  color: var(--text-secondary);
  margin-bottom: 8px;
`;

const ChangeIndicator = styled.div<{ $isPositive: boolean | null }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 500;
  padding: 4px 10px;
  border-radius: 20px;
  background: ${props =>
    props.$isPositive === null
      ? 'var(--background-color)'
      : props.$isPositive
        ? '#dcfce715'
        : '#fef2f215'};
  color: ${props =>
    props.$isPositive === null
      ? 'var(--text-secondary)'
      : props.$isPositive
        ? '#16a34a'
        : '#dc2626'};
  filter: saturate(0.7) brightness(0.85);
`;

const formatCurrency = (amount: number): string => {
  return `₪${amount.toLocaleString('he-IL', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

const AdminFinancialOverview: React.FC<Props> = ({ data }) => {
  const getChangeText = (change: number, isIncome: boolean = true): { text: string, isPositive: boolean | null } => {
    if (change === 0) {
      return { text: 'ללא שינוי', isPositive: null };
    }

    const prefix = change > 0 ? '▲' : '▼';
    const text = `${prefix} ${Math.abs(change).toFixed(1)}% מהחודש הקודם`;

    // For income, positive is good. For debts, negative is good.
    const isPositive = isIncome ? change > 0 : change < 0;

    return { text, isPositive };
  };

  const actualIncomeChange = getChangeText(data.actualIncomePercentageChange, true);
  const expectedIncomeChange = getChangeText(data.expectedIncomePercentageChange, true);
  const openDebtsChange = getChangeText(data.openDebtsPercentageChange, false);

  return (
    <Container>
      <SectionHeader>
        <SectionIcon>
          <Wallet size={20} />
        </SectionIcon>
        <SectionTitle>סקירה כלכלית</SectionTitle>
      </SectionHeader>

      <CardsGrid>
        <Card $accentColor="var(--success-color)">
          <CardHeader>
            <CardIcon $color="var(--success-color)">
              <Wallet size={20} />
            </CardIcon>
            <CardTitle>הכנסה החודש (בפועל)</CardTitle>
          </CardHeader>
          <CardValue>{formatCurrency(data.actualIncomeThisMonth)}</CardValue>
          <CardSubValue>
            חודש קודם: {formatCurrency(data.actualIncomeLastMonth)}
          </CardSubValue>
          <ChangeIndicator $isPositive={actualIncomeChange.isPositive}>
            {actualIncomeChange.text}
          </ChangeIndicator>
        </Card>

        <Card $accentColor="#6366f1">
          <CardHeader>
            <CardIcon $color="#6366f1" $noBackground>
              <TrendingUp size={20} />
            </CardIcon>
            <CardTitle>צפי חודש הבא</CardTitle>
          </CardHeader>
          <CardValue>{formatCurrency(data.expectedIncomeNextMonth)}</CardValue>
          <CardSubValue>על בסיס תוכניות תשלום פעילות</CardSubValue>
          {data.expectedIncomePercentageChange !== 0 && (
            <ChangeIndicator $isPositive={expectedIncomeChange.isPositive}>
              {expectedIncomeChange.text}
            </ChangeIndicator>
          )}
        </Card>

        <Card $accentColor="var(--warning-color)">
          <CardHeader>
            <CardIcon $color="var(--warning-color)">
              <CreditCard size={20} />
            </CardIcon>
            <CardTitle>יתרת חובות פתוחות</CardTitle>
          </CardHeader>
          <CardValue>{formatCurrency(data.openDebtsBalance)}</CardValue>
          <ChangeIndicator $isPositive={openDebtsChange.isPositive}>
            {openDebtsChange.text}
          </ChangeIndicator>
        </Card>

        <Card $accentColor="var(--primary-color)">
          <CardHeader>
            <CardIcon $color="var(--primary-color)">
              <FileText size={20} />
            </CardIcon>
            <CardTitle>תוכניות תשלום</CardTitle>
          </CardHeader>
          <CardValue>{data.activePaymentPlansCount}</CardValue>
          <CardSubValue>פעילות</CardSubValue>
          {data.overduePaymentPlansCount > 0 && (
            <ChangeIndicator $isPositive={false}>
              <AlertTriangle size={12} />
              {data.overduePaymentPlansCount} בפיגור
            </ChangeIndicator>
          )}
          {data.overduePaymentPlansCount === 0 && (
            <ChangeIndicator $isPositive={true}>
              <CheckCircle size={12} />
              אין פיגורים
            </ChangeIndicator>
          )}
        </Card>
      </CardsGrid>
    </Container>
  );
};

export default AdminFinancialOverview;
